/**
 * Navigation Debug Service
 * Tracks all navigation events and identifies full page reloads vs React Router navigation
 */

interface NavigationEvent {
  type: 'react-router' | 'window-location' | 'reload' | 'back-forward';
  from: string;
  to: string;
  timestamp: number;
  source?: string;
}

class NavigationDebugger {
  private events: NavigationEvent[] = [];
  private originalPushState: typeof history.pushState;
  private originalReplaceState: typeof history.replaceState;
  private isMonitoring = false;

  constructor() {
    this.originalPushState = history.pushState.bind(history);
    this.originalReplaceState = history.replaceState.bind(history);
  }

  startMonitoring() {
    if (this.isMonitoring) return;
    this.isMonitoring = true;

    // Monitor React Router navigation
    history.pushState = (state, title, url) => {
      this.logNavigation('react-router', window.location.href, url?.toString() || '', 'pushState');
      return this.originalPushState(state, title, url);
    };

    history.replaceState = (state, title, url) => {
      this.logNavigation('react-router', window.location.href, url?.toString() || '', 'replaceState');
      return this.originalReplaceState(state, title, url);
    };

    // Monitor back/forward navigation
    window.addEventListener('popstate', () => {
      this.logNavigation('back-forward', '', window.location.href, 'popstate');
    });

    // Monitor page unload (indicates full reload)
    window.addEventListener('beforeunload', () => {
      this.logNavigation('reload', window.location.href, '', 'beforeunload');
    });

    console.log('🧭 Navigation debugger started');
  }

  stopMonitoring() {
    if (!this.isMonitoring) return;
    this.isMonitoring = false;

    history.pushState = this.originalPushState;
    history.replaceState = this.originalReplaceState;
    
    console.log('🧭 Navigation debugger stopped');
  }

  logNavigation(type: NavigationEvent['type'], from: string, to: string, source?: string) {
    const event: NavigationEvent = {
      type,
      from,
      to,
      timestamp: Date.now(),
      source
    };

    this.events.push(event);
    
    // Keep only last 50 events
    if (this.events.length > 50) {
      this.events = this.events.slice(-50);
    }

    // Log with appropriate styling
    const style = this.getLogStyle(type);
    console.log(
      `%c🧭 [Navigation] ${type.toUpperCase()}`,
      style,
      {
        from: from || 'unknown',
        to: to || 'unknown',
        source,
        timestamp: new Date(event.timestamp).toISOString()
      }
    );

    // Alert on potentially problematic navigation
    if (type === 'window-location' || type === 'reload') {
      console.warn('⚠️ Full page navigation detected!', { type, from, to, source });
    }
  }

  private getLogStyle(type: NavigationEvent['type']): string {
    switch (type) {
      case 'react-router':
        return 'color: #22c55e; font-weight: bold;'; // Green
      case 'window-location':
        return 'color: #f59e0b; font-weight: bold;'; // Yellow
      case 'reload':
        return 'color: #ef4444; font-weight: bold;'; // Red
      case 'back-forward':
        return 'color: #3b82f6; font-weight: bold;'; // Blue
      default:
        return 'color: #6b7280; font-weight: bold;'; // Gray
    }
  }

  logWindowNavigation(to: string, source: string) {
    this.logNavigation('window-location', window.location.href, to, source);
  }

  logReload(source: string) {
    this.logNavigation('reload', window.location.href, '', source);
  }

  logRedirect(from: string, to: string, source: string) {
    this.logNavigation('react-router', from, to, source);
  }

  getNavigationHistory(): NavigationEvent[] {
    return [...this.events];
  }

  getRecentReloads(): NavigationEvent[] {
    return this.events.filter(e => e.type === 'reload' || e.type === 'window-location');
  }

  printReport() {
    console.group('🧭 Navigation Report');
    console.log('Total events:', this.events.length);
    
    const byType = this.events.reduce((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    console.table(byType);
    
    const recentReloads = this.getRecentReloads();
    if (recentReloads.length > 0) {
      console.warn('Recent problematic navigations:', recentReloads);
    }
    
    console.groupEnd();
  }
}

export const navigationDebugger = new NavigationDebugger();

// Auto-start in development (disabled by default - enable with VITE_ENABLE_NAV_DEBUG=true)
if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_NAV_DEBUG === 'true') {
  navigationDebugger.startMonitoring();
}
